import { useState, useEffect } from 'react';
import { Table, Card, ScrollArea, LoadingOverlay, Box } from '@mantine/core';
import { SortableTableHeader, NonSortableColumnHeader } from '../../common/SortableTableHeader';
import { CollapsibleGroupHeader } from '../components/CollapsibleGroupHeader';
import { flattenGroups, type GroupNode } from '../../../utils/groupDataByField';
import { Pagination } from '../../common/Pagination';
import type { SortConfig, ColumnConfig } from '../../../hooks/useColumnConfig';
import { renderOneOnOneCell, OneOnOneNotesModal } from './OneOnOneCellRenderers';
import type { OneOnOneDisplayRow } from './useDataLibraryOneOnOnes';
import classes from '../../common/ConfigurableTable/ConfigurableTable.module.css';
import { ResizableHeaderCell } from '../components/ResizableHeaderCell';
import { useDataLibraryColumnWidths } from '../useDataLibraryTableLayout';
import { ColumnWidthCell } from '../components/ColumnWidthCell';
import { DataLibraryEmptyState } from '../components/DataLibraryEmptyState';

interface OneOnOnesTableProps {
  data: OneOnOneDisplayRow[];
  groupedData: GroupNode<OneOnOneDisplayRow>[] | null;
  columns: ColumnConfig[];
  sortConfig: SortConfig | null;
  onSort: (key: string) => void;
  loading: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const NOTE_COLUMNS = new Set(['attendance_notes', 'session_notes', 'feedback']);

function getColStyle(colId: string): React.CSSProperties {
  return { minWidth: NOTE_COLUMNS.has(colId) ? 280 : 160, whiteSpace: 'nowrap', overflow: 'hidden' };
}

export function OneOnOnesTable({
  data,
  groupedData,
  columns,
  sortConfig,
  onSort,
  loading,
  hasActiveFilters,
  onClearFilters,
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: OneOnOnesTableProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [notesRow, setNotesRow] = useState<OneOnOneDisplayRow | null>(null);
  const layout = useDataLibraryColumnWidths();

  const groupKeys = groupedData ? groupedData.map(g => g.key).join('\0') : '';
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [groupKeys]);

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderRow = (row: OneOnOneDisplayRow) => (
    <Table.Tr key={row.session_id} className={classes.tableRow}>
      {columns.map((col, index) => (
        <ColumnWidthCell
          key={col.id}
          className={index === 0 ? classes.stickyColumn : undefined}
          style={getColStyle(col.id)}
          width={layout.columnWidths[col.id]}
          // A note preview opens the full text in a modal, and the email pill has its own Tooltip.
          hasTooltip={NOTE_COLUMNS.has(col.id) || col.id === 'participant_email'}
        >
          {renderOneOnOneCell(row, col.id, setNotesRow)}
        </ColumnWidthCell>
      ))}
    </Table.Tr>
  );

  const hasData = data.length > 0;
  const showEmpty = !loading && !hasData;

  return (
    <Card withBorder p={0} radius={8} bg="white" style={{ boxShadow: 'none', position: 'relative' }} className={classes.tableCard}>
      <LoadingOverlay visible={loading} overlayProps={{ blur: 1 }} />
      <ScrollArea scrollbarSize={8} type="auto" offsetScrollbars={false}>
        <Table highlightOnHover className={classes.table}>
          <Table.Thead className={classes.tableHeader}>
            <Table.Tr>
              {columns.map((col, index) => (
                <ResizableHeaderCell key={col.id} column={col} sticky={index === 0} style={getColStyle(col.id)} layout={layout}>
                  {col.sortable ? (
                    <SortableTableHeader label={col.label} sortKey={col.id} currentSort={sortConfig} onSort={onSort} />
                  ) : (
                    <NonSortableColumnHeader label={col.label} />
                  )}
                </ResizableHeaderCell>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groupedData
                ? flattenGroups(groupedData, collapsedGroups).map(item =>
                    'group' in item ? (
                      <CollapsibleGroupHeader
                        key={`group-${item.group.key}`}
                        label={item.group.label}
                        count={item.group.rows.length}
                        depth={item.group.depth}
                        colSpan={columns.length}
                        isCollapsed={collapsedGroups.has(item.group.key)}
                        onToggle={() => toggleGroup(item.group.key)}
                        entityLabel={item.group.rows.length === 1 ? '1:1' : '1:1s'}
                      />
                    ) : (
                      renderRow(item.row)
                    ),
                  )
                : data.map(renderRow)}
          </Table.Tbody>
        </Table>
      </ScrollArea>
      {showEmpty && <DataLibraryEmptyState message="No 1:1 sessions in these cohorts" hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters} />}
      {!groupedData && totalCount > 0 && (
        <Box className={classes.paginationWrapper}>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={totalCount}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={[25, 50, 100, 250]}
            entityLabel="1:1s"
          />
        </Box>
      )}
      <OneOnOneNotesModal row={notesRow} onClose={() => setNotesRow(null)} />
    </Card>
  );
}
